import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const emptyReq = {
  make: "",
  model: "",
  year_min: "",
  year_max: "",
  notes: "",
  status: "active",
};

const statusMeta = {
  active: { label: "Active", className: "bg-blue-50 text-blue-700 border-blue-200" },
  fulfilled: { label: "Fulfilled", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", className: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

const TRANSMISSION_LABELS = { manual: "Manual", automatic: "Automatic" };
const STEERING_LABELS = { lhd: "Left Hand Drive", rhd: "Right Hand Drive" };
const TIME_FRAME_LABELS = { now: "Ready to Buy Now", "3_6": "3 to 6 months", "6_12": "6 to 12 months" };
const PAYMENT_LABELS = { cash: "Cash", financing: "Financing" };
const ENGINE_LABELS = { na: "NA / Non Turbo", turbo: "Turbo / Twin Turbo" };

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const [reqOpen, setReqOpen] = useState(false);
  const [reqEditing, setReqEditing] = useState(null);
  const [reqForm, setReqForm] = useState(emptyReq);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    setEditForm({
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      desired_ymm: customer.desired_ymm || "",
      engine_type: customer.engine_type || "",
      transmission: customer.transmission || "",
      steering: customer.steering || "",
      colors: customer.colors || "",
      time_frame: customer.time_frame || "",
      payment: customer.payment || "",
      notes: customer.notes || "",
      budget_min: customer.budget_min == null ? "" : String(customer.budget_min),
      budget_max: customer.budget_max == null ? "" : String(customer.budget_max),
    });
    setEditOpen(true);
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        email: editForm.email.trim(),
        desired_ymm: editForm.desired_ymm.trim(),
        engine_type: editForm.engine_type,
        transmission: editForm.transmission,
        steering: editForm.steering,
        colors: editForm.colors.trim(),
        time_frame: editForm.time_frame,
        payment: editForm.payment,
        notes: editForm.notes,
        budget_min: editForm.budget_min === "" ? null : Number(editForm.budget_min),
        budget_max: editForm.budget_max === "" ? null : Number(editForm.budget_max),
      };
      await api.patch(`/customers/${id}`, payload);
      toast.success("Customer updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async () => {
    if (!window.confirm(`Delete customer "${customer.name}" and all their requests?`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/crm");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCreateReq = () => {
    setReqEditing(null);
    setReqForm(emptyReq);
    setReqOpen(true);
  };

  const openEditReq = (r) => {
    setReqEditing(r);
    setReqForm({
      make: r.make || "",
      model: r.model || "",
      year_min: r.year_min == null ? "" : String(r.year_min),
      year_max: r.year_max == null ? "" : String(r.year_max),
      notes: r.notes || "",
      status: r.status || "active",
    });
    setReqOpen(true);
  };

  const saveReq = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        make: reqForm.make.trim(),
        model: reqForm.model.trim(),
        year_min: reqForm.year_min === "" ? null : Number(reqForm.year_min),
        year_max: reqForm.year_max === "" ? null : Number(reqForm.year_max),
        notes: reqForm.notes,
        status: reqForm.status,
      };
      if (reqEditing) {
        await api.patch(`/customer-requests/${reqEditing.id}`, payload);
        toast.success("Request updated");
      } else {
        const { data } = await api.post(`/customers/${id}/requests`, payload);
        toast.success("Request added");
        if (data?.matches?.length > 0) {
          toast.info(`Found ${data.matches.length} matching vehicle${data.matches.length > 1 ? "s" : ""}!`, { duration: 6000 });
        }
      }
      setReqOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteReq = async (r) => {
    if (!window.confirm(`Delete request "${r.make} ${r.model}"?`)) return;
    try {
      await api.delete(`/customer-requests/${r.id}`);
      toast.success("Request deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateReqStatus = async (r, status) => {
    try {
      await api.patch(`/customer-requests/${r.id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading || !customer) {
    return <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 text-sm text-zinc-500">Loading customer...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-6xl">
      <Link to="/crm" className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-950 mb-4" data-testid="back-to-crm">
        <ArrowLeft className="w-3 h-3" /> Back to CRM
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 pb-6 border-b border-zinc-200">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-2 font-mono">
            Customer
          </div>
          <h1 className="font-heading text-4xl tracking-tighter font-black text-zinc-950" data-testid="customer-title">{customer.name}</h1>
          <div className="mt-1 text-sm text-zinc-600 space-x-2 font-mono">
            {customer.phone && <span>{customer.phone}</span>}
            {customer.phone && customer.email && <span className="text-zinc-300">·</span>}
            {customer.email && <span>{customer.email}</span>}
            {!customer.phone && !customer.email && <span className="text-zinc-300">no contact on file</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openEdit} data-testid="edit-customer-btn" className="rounded-sm border-zinc-300">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button variant="outline" onClick={deleteCustomer} data-testid="delete-customer-btn" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Year Make Model</div>
          <div className="font-heading text-lg font-black mt-2" data-testid="customer-ymm">
            {customer.desired_ymm || <span className="text-zinc-300 font-normal">—</span>}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Engine</div>
          <div className="font-heading text-lg font-black mt-2" data-testid="customer-engine">
            {customer.engine_type === "na" ? "NA / Non Turbo" : customer.engine_type === "turbo" ? "Turbo / Twin Turbo" : <span className="text-zinc-300 font-normal">—</span>}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Budget</div>
          <div className="font-heading text-lg font-black mt-2 font-mono">
            {customer.budget_min == null && customer.budget_max == null ? <span className="text-zinc-300 font-normal">—</span> : `${customer.budget_min != null ? currency(customer.budget_min) : "any"} – ${customer.budget_max != null ? currency(customer.budget_max) : "any"}`}
          </div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-800">Live Matches</div>
          <div className="font-heading text-2xl font-black mt-2 text-amber-900" data-testid="customer-match-count">{customer.match_count || 0}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-amber-700 mt-1">
            {customer.active_request_count || 0} active · {customer.request_count || 0} total req.
          </div>
        </div>
      </div>

      {customer.notes && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Notes</div>
          <div className="text-sm text-zinc-800 whitespace-pre-wrap mt-2">{customer.notes}</div>
        </div>
      )}

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid="customer-preferences">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-3">Preferences</div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Engine</dt>
            <dd className="text-zinc-900 mt-1">{ENGINE_LABELS[customer.engine_type] || <span className="text-zinc-300">—</span>}</dd>
          </div>
          <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Transmission</dt>
            <dd className="text-zinc-900 mt-1">{TRANSMISSION_LABELS[customer.transmission] || <span className="text-zinc-300">—</span>}</dd>
          </div>
          <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Steering</dt>
            <dd className="text-zinc-900 mt-1">{STEERING_LABELS[customer.steering] || <span className="text-zinc-300">—</span>}</dd>
          </div>
          <div className="md:col-span-3">
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Colors</dt>
            <dd className="text-zinc-900 mt-1">{customer.colors || <span className="text-zinc-300">—</span>}</dd>
          </div>
          <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Time Frame</dt>
            <dd className="text-zinc-900 mt-1">{TIME_FRAME_LABELS[customer.time_frame] || <span className="text-zinc-300">—</span>}</dd>
          </div>
          <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Payment</dt>
            <dd className="text-zinc-900 mt-1">{PAYMENT_LABELS[customer.payment] || <span className="text-zinc-300">—</span>}</dd>
          </div>
        </dl>
      </div>

      <section className="mt-8 bg-white border border-zinc-200 rounded-sm">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Vehicle requests</h2>
          <Dialog open={reqOpen} onOpenChange={setReqOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openCreateReq} data-testid="add-request-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Request
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">
                  {reqEditing ? "Edit request" : "New vehicle request"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={saveReq} className="space-y-4" data-testid="request-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Make</Label>
                    <Input value={reqForm.make} onChange={(e) => setReqForm({ ...reqForm, make: e.target.value })} placeholder="Toyota" data-testid="request-make-input" className="rounded-sm border-zinc-300" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Model</Label>
                    <Input value={reqForm.model} onChange={(e) => setReqForm({ ...reqForm, model: e.target.value })} placeholder="Supra" data-testid="request-model-input" className="rounded-sm border-zinc-300" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year From</Label>
                    <Input type="number" value={reqForm.year_min} onChange={(e) => setReqForm({ ...reqForm, year_min: e.target.value })} placeholder="(any)" data-testid="request-year-min-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year To</Label>
                    <Input type="number" value={reqForm.year_max} onChange={(e) => setReqForm({ ...reqForm, year_max: e.target.value })} placeholder="(any)" data-testid="request-year-max-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Status</Label>
                  <Select value={reqForm.status} onValueChange={(v) => setReqForm({ ...reqForm, status: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="request-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="fulfilled">Fulfilled</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes (color, trim, mileage, etc.)</Label>
                  <Textarea value={reqForm.notes} onChange={(e) => setReqForm({ ...reqForm, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={3} placeholder="e.g. Prefers white, under 50k miles, manual transmission" />
                </div>
                <p className="text-xs text-zinc-500">
                  Tip: leave Make/Model blank for a free-form request (won&apos;t auto-match). Fill both to enable matching.
                </p>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setReqOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="request-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Saving..." : reqEditing ? "Save changes" : "Add request"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {(!customer.requests || customer.requests.length === 0) && (
          <div className="px-6 py-10 text-center text-sm text-zinc-500">
            No requests yet. Click &quot;Add Request&quot; to track what this customer is looking for.
          </div>
        )}

        <ul className="divide-y divide-zinc-200">
          {(customer.requests || []).map((r) => (
            <li key={r.id} className="px-6 py-4" data-testid={`request-row-${r.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-zinc-950">
                      {r.make || r.model ? `${r.make} ${r.model}`.trim() : <span className="italic font-normal text-zinc-500">Free-form request</span>}
                    </div>
                    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm border ${(statusMeta[r.status] || statusMeta.active).className}`}>
                      {(statusMeta[r.status] || statusMeta.active).label}
                    </span>
                    {(r.year_min || r.year_max) && (
                      <span className="text-xs text-zinc-500 font-mono">{r.year_min || "any"}–{r.year_max || "any"}</span>
                    )}
                    {r.matches && r.matches.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm border bg-amber-50 text-amber-800 border-amber-300">
                        <Sparkles className="w-3 h-3" /> {r.matches.length} match{r.matches.length > 1 ? "es" : ""}
                      </span>
                    )}
                  </div>
                  {r.notes && (
                    <div className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{r.notes}</div>
                  )}
                  {r.matches && r.matches.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.matches.map((v) => (
                        <Link key={v.id} to={`/vehicles/${v.id}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-sm text-xs font-bold text-amber-900 hover:bg-amber-200" data-testid={`request-match-${v.id}`}>
                          <span>{v.year} {v.make} {v.model}</span>
                          <span className="font-mono text-amber-800">{v.sale_price != null ? currency(v.sale_price) : currency(v.purchase_price)}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {r.status === "active" && (
                    <button onClick={() => updateReqStatus(r, "fulfilled")} title="Mark fulfilled" data-testid={`fulfill-request-${r.id}`} className="text-zinc-400 hover:text-emerald-600 p-1.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  {r.status === "active" && (
                    <button onClick={() => updateReqStatus(r, "cancelled")} title="Cancel" data-testid={`cancel-request-${r.id}`} className="text-zinc-400 hover:text-zinc-700 p-1.5">
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => openEditReq(r)} data-testid={`edit-request-${r.id}`} className="text-zinc-400 hover:text-zinc-950 p-1.5">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteReq(r)} data-testid={`delete-request-${r.id}`} className="text-zinc-400 hover:text-[#FF2400] p-1.5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Edit customer</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveCustomer} className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Phone</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Email</Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-sm border-zinc-300" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year Make Model</Label>
                <Input value={editForm.desired_ymm} onChange={(e) => setEditForm({ ...editForm, desired_ymm: e.target.value })} placeholder="Ex 1993 Toyota Supra" className="rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Engine</Label>
                <Select value={editForm.engine_type || "none"} onValueChange={(v) => setEditForm({ ...editForm, engine_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300">
                    <SelectValue placeholder="Select engine type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="na">NA / Non Turbo</SelectItem>
                    <SelectItem value="turbo">Turbo / Twin Turbo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Transmission</Label>
                  <Select value={editForm.transmission || "none"} onValueChange={(v) => setEditForm({ ...editForm, transmission: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Steering</Label>
                  <Select value={editForm.steering || "none"} onValueChange={(v) => setEditForm({ ...editForm, steering: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="lhd">Left Hand Drive (If Applicable)</SelectItem>
                      <SelectItem value="rhd">Right Hand Drive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Colors</Label>
                <Input value={editForm.colors} onChange={(e) => setEditForm({ ...editForm, colors: e.target.value })} placeholder="e.g. White, Black, Silver" className="rounded-sm border-zinc-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Time Frame</Label>
                  <Select value={editForm.time_frame || "none"} onValueChange={(v) => setEditForm({ ...editForm, time_frame: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="now">Ready to Buy Now</SelectItem>
                      <SelectItem value="3_6">3 to 6 months</SelectItem>
                      <SelectItem value="6_12">6 to 12 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Payment</Label>
                  <Select value={editForm.payment || "none"} onValueChange={(v) => setEditForm({ ...editForm, payment: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="financing">Financing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Min</Label>
                  <Input type="number" step="0.01" value={editForm.budget_min} onChange={(e) => setEditForm({ ...editForm, budget_min: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Max</Label>
                  <Input type="number" step="0.01" value={editForm.budget_max} onChange={(e) => setEditForm({ ...editForm, budget_max: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={3} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="customer-edit-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
